import { IStringTool } from "../core/IStringTool";
import { ISplitOptionItem } from "../models/text/ISplitOptionItem";
import { ISplitOptions } from "../models/text/ISplitOptions";
import { SplitOptionsParserInput } from "../models/text/SplitOptionsParserInput";

/**
 * Tool responsible for parsing the string value of a split attribute
 * (e.g., "line[center]|char[random(0,10)|abs]") into a structured
 * ISplitOptions object.
 * Implements the IStringTool interface.
 */
export class SplitOptionsParserTool
  implements IStringTool<SplitOptionsParserInput, ISplitOptions>
{
  /**
   * Parses the attribute string into an ISplitOptions object.
   * Handles splitting by '|', parsing prefixes (word-, char-), main types (line, word, char),
   * and parameters within brackets (align, random, abs).
   *
   * @param input - An object containing the attributeValue string (can be null).
   * @returns An ISplitOptions object representing the parsed rules.
   * Returns an object with empty arrays if the attributeValue is null or empty.
   */
  process({ attributeValue }: SplitOptionsParserInput): ISplitOptions {
    // Initialize with empty arrays for all possible options
    const options: ISplitOptions = {
      line: [],
      word: [],
      char: [],
      charLine: [],
      charWord: [],
      wordLine: [],
    };

    // Return default empty options if attribute value is null or empty string
    if (!attributeValue) {
      return options;
    }

    // Split the main attribute value by the pipe '|' separator
    const parts = attributeValue.split("|");

    parts.forEach((part) => {
      // Trim whitespace from each part
      const trimmedPart = part.trim();
      if (!trimmedPart) return; // Skip empty parts resulting from separators like '||' or trailing '|'

      // Regex to capture:
      // 1. Optional prefix (word-, char-) - (\w+-)?
      // 2. Main option type (line, word, char) - (\w+)
      // 3. Optional parameters section including brackets - (\[(.*?)\])?
      // 4. Content within the brackets (params string) - (.*?)
      const match = trimmedPart.match(/^(\w+-)?(\w+)(\[(.*?)\])?$/);

      if (match) {
        const prefix = match[1] || ""; // e.g., "word-" or ""
        const optionType = match[2]; // e.g., "line", "word", "char"
        const fullOptionKey = prefix + optionType; // Combined key like "line", "word", "charLine"
        // Extract params string inside brackets, or empty string if no brackets
        const paramsString = match[4] || "";
        // Split params by comma and trim each resulting param string
        const params = paramsString
          .split(";")
          .map((p) => p.trim())
          .filter((p) => p.length > 0); // Filter out empty params

        // Parse the parameters within brackets using the helper method
        const parsedParam: ISplitOptionItem = this.parseParamsArray(params);

        // Add the parsed parameters object to the correct array in the options object
        // Use a type assertion for simplicity. More complex type guards could be used.
        switch (fullOptionKey) {
          case "line":
            (options.line as ISplitOptionItem[]).push(parsedParam);
            break;
          case "word":
            (options.word as ISplitOptionItem[]).push(parsedParam);
            break;
          case "char":
            (options.char as ISplitOptionItem[]).push(parsedParam);
            break;
          case "charLine": // e.g., char-line
            (options.charLine as ISplitOptionItem[]).push(parsedParam);
            break;
          case "charWord": // e.g., char-word
            (options.charWord as ISplitOptionItem[]).push(parsedParam);
            break;
          case "wordLine": // e.g., word-line
            (options.wordLine as ISplitOptionItem[]).push(parsedParam);
            break;
          default:
            // Log a warning for unrecognized option combinations
            console.warn(
              `SplitOptionsParserTool: Unrecognized option type "${fullOptionKey}" in part "${trimmedPart}"`
            );
            break;
        }
      } else {
        // Log a warning for parts that don't match the expected format "type[params]" or "prefix-type[params]"
        console.warn(
          `SplitOptionsParserTool: Could not parse part format "${trimmedPart}"`
        );
      }
    }); // End forEach part

    return options; // Return the populated options object
  }

  /**
   * Parses an array of string parameters (extracted from within brackets `[...]`).
   * Determines alignment, random settings, and absolute flag.
   * Example input: ['center'], ['random(0, 10)', 'abs']
   *
   * @param params - An array of string parameters.
   * @returns An ISplitOptionItem object representing the parsed parameters.
   */
  private parseParamsArray(params: string[]): ISplitOptionItem {
    // Default result with 'start' alignment
    const result: ISplitOptionItem = { align: "start" };

    params.forEach((param) => {
      if (param === "abs") {
        // Set the absolute flag
        result.abs = true;
      } else if (param.startsWith("random")) {
        // Handle random alignment
        result.align = "random"; // Set alignment type
        // Try to parse min/max values like random(10,50)
        // Allows optional whitespace around numbers and comma
        const randomMatch = param.match(/random\(\s*(\d+)\s*,\s*(\d+)\s*\)/);
        if (randomMatch) {
          // If numbers found, parse and store them
          result.random = {
            min: parseInt(randomMatch[1], 10),
            max: parseInt(randomMatch[2], 10),
          };
          // Optional: Add validation like min <= max here if desired
        }
        // If 'random' specified without valid numbers, result.align is 'random',
        // and result.random remains undefined (defaults will apply later based on context).
      } else if (["start", "center", "end"].includes(param)) {
        // Handle specific alignment keywords
        result.align = param;
      }
      // Silently ignore any other unrecognized parameters within the brackets
    });

    return result; // Return the parsed option item definition
  }
}
